import React, { useEffect, useState } from "react";
import { Context, Hooks } from "@xt/xtbase";
import { getReferralRule } from "@/api/referral";
import {
  formatEffectiveUserSummary,
  formatReferralRebateExampleDesc,
  formatReferralRebateExampleTitle,
  formatReferralRewardTierLabel,
} from "@/components/Pages/referral/utils/formatEffectiveUserSummary";
import { Spin } from "antd";
import styles from "./index.module.less";

const { useTranslation } = Hooks;
const { XtContext } = Context;

/** 邀请规则活动 key：`/sapi/v4/activity/invite-rules/{activity}`，可替换 */
const REFERRAL_RULE_ACTIVITY_KEY = "INVITE_DEPOSIT_TRADE_REWARD";

export default function ReferralRules() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus } = appState;

  const [rulePayload, setRulePayload] = useState(null);
  const [ruleErr, setRuleErr] = useState(null);
  const [ruleLoading, setRuleLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRuleLoading(true);
      setRuleErr(null);
      try {
        const data = await getReferralRule({
          activity: REFERRAL_RULE_ACTIVITY_KEY,
          isPublic: loginStatus !== 1,
        });
        if (!cancelled) setRulePayload(data);
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.message ||
            e?.mc ||
            (typeof e === "string" ? e : e != null ? JSON.stringify(e, null, 2) : String(e));
          setRuleErr(msg);
        }
      } finally {
        if (!cancelled) setRuleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loginStatus]);
  // invite-rules 接口：字段在 result 顶层；旧 parse 接口：包在 invitationRules 下
  const rules = rulePayload?.invitationRules ?? rulePayload;
  const effectiveUser = rules?.effectiveUser;
  const effectiveUserSummaryText = formatEffectiveUserSummary(effectiveUser, t);
  const rewardTiers = Array.isArray(rules?.rewardTiers) ? rules.rewardTiers : null;
  const rebateExamples = Array.isArray(rules?.rebateExamples) ? rules.rebateExamples : null;

  const formatRewardAmount = (amount, currency) => {
    if (amount == null || amount === "") return "";
    return currency ? `${amount} ${currency}` : String(amount);
  };

  return (
    <section className={styles.block}>
      <div className={styles.card}>
        <Spin spinning={ruleLoading}>
          {!!effectiveUserSummaryText && (
            <div className={styles.highlightBox}>
              <div className={styles.highlightRow}>
                <span className={styles.highlightLabel}>{t("home.referralRulesActiveUserLabel")}</span>
                <span className={styles.highlightDesc}>{effectiveUserSummaryText}</span>
              </div>
            </div>
          )}

          {Array.isArray(rewardTiers) && rewardTiers.length > 0 && (
            <div className={styles.rateList}>
              {rewardTiers.map((tier, idx) => (
                <div className={styles.rateRow} key={`${tier?.upstreamLevel || idx}-${tier?.tierLabel || ""}`}>
                  <span className={styles.rateLabel}>{formatReferralRewardTierLabel(tier, t)}</span>
                  <span className={styles.rateValue}>{formatRewardAmount(tier?.rewardAmount, tier?.rewardCurrency)}</span>
                </div>
              ))}
            </div>
          )}

          {Array.isArray(rebateExamples) && rebateExamples.length > 0 && (
            <div className={styles.exampleBlock}>
              <h4 className={styles.exampleTitle}>{t("home.referralRulesExampleTitle")}</h4>
              <div className={styles.tableOuter}>
                <div className={styles.table}>
                  <div className={styles.tableHead}>
                    <div className={styles.th}>{t("home.referralRulesColExample")}</div>
                    <div className={`${styles.th} ${styles.thGrow}`}>{t("home.referralRulesColDesc")}</div>
                    <div className={`${styles.th} ${styles.thEnd}`}>{t("home.referralRulesColReward")}</div>
                  </div>
                  <div className={styles.tableBody}>
                    {rebateExamples.map((example, idx) => (
                      <div className={styles.tr} key={`${example?.exampleTitle || "example"}-${idx}`}>
                        <div className={styles.td}>{formatReferralRebateExampleTitle(example, idx, t)}</div>
                        <div className={`${styles.td} ${styles.tdMuted}`}>
                          {formatReferralRebateExampleDesc(example, idx, t)}
                        </div>
                        <div className={`${styles.td} ${styles.tdEnd} ${styles.tdReward}`}>
                          {formatRewardAmount(example?.rewardAmount, example?.rewardCurrency)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <ul className={styles.notes}>
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className={styles.noteItem}>
                <span className={styles.noteIndex}>{n}.</span>
                <span className={styles.noteText}>{t(`home.referralRulesNote${n}`)}</span>
              </li>
            ))}
          </ul>
        </Spin>
      </div>
    </section>
  );
}
